import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useFetch from "hooks/useFetch";
import { RoutePaths } from "enums/RoutePaths";
import {
  Badge,
  Button,
  Card,
  Column,
  EmptyState,
  Icon,
  PageHeader,
  Pagination,
  Select,
  Skeleton,
  Table,
  TextField,
  Textarea,
  styles as ui,
  useConfirm,
  useToast,
} from "stories/Admin";
import SectionTabs from "pages/AdminLayout/SectionTabs";
import { formatOrgNumber } from "pages/AdminLayout/menuConfig";
import {
  errorText,
  formatOslo,
  MosqueSuggestion,
  MosqueSuggestionPage,
  platformLabel,
  replyMailto,
  requestInquiryRefresh,
  suggestionPrefill,
  SUGGESTIONS_BASE,
  suggestionsQuery,
  suggestionStatusInfo,
} from "./inquiries";
import styles from "./Inquiries.module.scss";

/*
 * Superadmin: forslag om moskeer som mangler i iBønn, fra appene («Registrer en moské») og
 * ibønn.no/for-moskeer. Status, notat, kobling til org.nr når moskeen er lagt inn, og en snarvei til
 * «Opprett moské» med feltene forhåndsutfylt.
 */

const PAGE_SIZE = 50;

const statusOptions = [
  { value: "NEW,IN_PROGRESS", label: "Åpne" },
  { value: "NEW", label: "Nye" },
  { value: "IN_PROGRESS", label: "Under arbeid" },
  { value: "ADDED", label: "Lagt inn" },
  { value: "REJECTED", label: "Avvist" },
  { value: "", label: "Alle" },
];

type Patch = { status?: string; adminNote?: string; linkedOrgNr?: string };

const MosqueSuggestionsPage: React.FC = () => {
  const { execute, lastError } = useFetch<any>();
  const executeRef = useRef(execute);
  executeRef.current = execute;
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();

  const [data, setData] = useState<MosqueSuggestionPage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [status, setStatus] = useState("NEW,IN_PROGRESS");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<MosqueSuggestion | null>(null);
  const [note, setNote] = useState("");
  const [orgNr, setOrgNr] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setIsLoading(true);
    const result = (await executeRef.current(
      suggestionsQuery({ status, q: query, page: page - 1, size: PAGE_SIZE }),
      "GET",
    )) as MosqueSuggestionPage | null;
    if (result) setData(result);
    setFailed(!result);
    setIsLoading(false);
  }, [status, query, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setNote(selected?.adminNote ?? "");
    setOrgNr(selected?.linkedOrgNr ?? "");
  }, [selected?.id]);

  const patch = async (s: MosqueSuggestion, body: Patch, action: string) => {
    setBusy(action);
    const updated = (await executeRef.current(
      `${SUGGESTIONS_BASE}/${s.id}`,
      "PATCH",
      body,
    )) as MosqueSuggestion | null;
    setBusy(null);
    if (!updated) {
      toast.error(errorText(lastError(), "Kunne ikke lagre endringen."));
      return null;
    }
    setSelected(updated);
    setData((d) =>
      d
        ? {
            ...d,
            items: d.items.map((x) => (x.id === updated.id ? updated : x)),
          }
        : d,
    );
    requestInquiryRefresh();
    return updated;
  };

  const markAdded = async (s: MosqueSuggestion) => {
    const value = orgNr.replace(/\s+/g, "");
    if (!value) {
      toast.error("Skriv org.nr for moskeen slik den er lagt inn i iBønn.");
      return;
    }
    if (await patch(s, { status: "ADDED", linkedOrgNr: value }, "added")) {
      toast.success("Forslaget er merket som lagt inn.");
    }
  };

  const remove = async (s: MosqueSuggestion) => {
    const ok = await confirm({
      title: "Slett forslaget?",
      message: (
        <>
          Forslaget om <b>{s.name}</b> slettes permanent. Dette kan ikke angres.
        </>
      ),
      confirmLabel: "Slett",
      tone: "danger",
    });
    if (!ok) return;
    setBusy("delete");
    await executeRef.current(`${SUGGESTIONS_BASE}/${s.id}`, "DELETE");
    const error = lastError();
    setBusy(null);
    if (error) {
      toast.error(errorText(error, "Kunne ikke slette forslaget."));
      return;
    }
    toast.success("Forslaget er slettet.");
    setSelected(null);
    requestInquiryRefresh();
    load();
  };

  const columns: Array<Column<MosqueSuggestion>> = [
    {
      key: "mosque",
      header: "Moské",
      render: (s) => (
        <span className={styles.stack}>
          <b className={styles.clamp}>{s.name}</b>
          <span className={styles.muted}>{s.city}</span>
        </span>
      ),
    },
    {
      key: "contact",
      header: "Kontakt",
      render: (s) => (
        <span className={styles.stack}>
          <span className={styles.clamp}>
            {s.contactName || s.contactEmail || s.contactPhone || "–"}
          </span>
          {s.contactName && (s.contactEmail || s.contactPhone) && (
            <span className={styles.muted}>
              {s.contactEmail || s.contactPhone}
            </span>
          )}
        </span>
      ),
    },
    {
      key: "affiliated",
      header: "Representerer",
      render: (s) => (s.isAffiliated ? "Ja" : "Nei"),
    },
    {
      key: "platform",
      header: "Fra",
      render: (s) => platformLabel(s.platform),
    },
    {
      key: "time",
      header: "Tid",
      numeric: true,
      render: (s) => formatOslo(s.createdAt),
    },
    {
      key: "status",
      header: "Status",
      render: (s) => {
        const info = suggestionStatusInfo[s.status];
        return (
          <Badge tone={info?.tone ?? "grey"}>{info?.label ?? s.status}</Badge>
        );
      },
    },
  ];

  const pageCount = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  return (
    <>
      <PageHeader
        title="Henvendelser"
        subtitle="Forslag om moskeer som mangler i iBønn, fra appene og ibønn.no/for-moskeer."
        actions={
          <Button
            variant="secondary"
            icon={<Icon name="swap" size={16} />}
            onClick={() => {
              load();
              requestInquiryRefresh();
            }}
            loading={isLoading}
          >
            Oppdater
          </Button>
        }
      />
      <SectionTabs />

      <div className={styles.split}>
        <Card className={styles.tableCard}>
          <div className={styles.filters}>
            <TextField
              label="Søk"
              placeholder="Søk i navn, by, kontaktinfo, melding eller notat"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fieldClassName={styles.searchField}
            />
            <Select
              label="Status"
              value={status}
              options={statusOptions}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
            />
          </div>

          {failed && !data ? (
            <EmptyState icon="alert" title="Kunne ikke hente forslagene">
              Prøv igjen med «Oppdater».
            </EmptyState>
          ) : isLoading && !data ? (
            <div className={styles.skeletons}>
              {Array.from({ length: 5 }, (_, i) => (
                <Skeleton key={i} height={44} />
              ))}
            </div>
          ) : (
            <>
              <Table
                columns={columns}
                rows={data?.items ?? []}
                rowKey={(s) => s.id}
                onRowClick={setSelected}
                rowState={(s) =>
                  s.id === selected?.id
                    ? "on"
                    : s.status === "ADDED" || s.status === "REJECTED"
                      ? "dim"
                      : undefined
                }
                caption="Moskéforslag"
                empty={
                  <EmptyState icon="mosque" title="Ingen forslag">
                    Ingen forslag passer filtrene.
                  </EmptyState>
                }
              />
              <div className={styles.footer}>
                <span className={styles.muted}>
                  {data
                    ? `${data.total.toLocaleString("nb-NO")} forslag · ${data.newCount.toLocaleString("nb-NO")} nye`
                    : ""}
                </span>
                <Pagination
                  page={page}
                  pageCount={pageCount}
                  onChange={setPage}
                />
              </div>
            </>
          )}
        </Card>

        <div className={styles.detail}>
          {selected ? (
            <Card
              title={selected.name}
              actions={
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelected(null)}
                  aria-label="Lukk"
                >
                  <Icon name="x" size={16} />
                </Button>
              }
            >
              <div className={styles.detailHead}>
                <Badge
                  tone={suggestionStatusInfo[selected.status]?.tone ?? "grey"}
                >
                  {suggestionStatusInfo[selected.status]?.label ??
                    selected.status}
                </Badge>
                <span className={styles.muted}>
                  {formatOslo(selected.createdAt)} ·{" "}
                  {platformLabel(selected.platform)}
                  {selected.appVersion && ` ${selected.appVersion}`}
                </span>
              </div>

              <div className={styles.actions}>
                <Button
                  size="sm"
                  icon={<Icon name="plus" size={16} />}
                  onClick={() =>
                    navigate(RoutePaths.MosqueAdmin, {
                      state: { prefill: suggestionPrefill(selected) },
                    })
                  }
                >
                  Opprett moské
                </Button>
                {selected.status !== "IN_PROGRESS" &&
                  selected.status !== "ADDED" && (
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={busy === "progress"}
                      onClick={() =>
                        patch(selected, { status: "IN_PROGRESS" }, "progress")
                      }
                    >
                      Under arbeid
                    </Button>
                  )}
                {selected.status !== "NEW" && (
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={busy === "new"}
                    onClick={() => patch(selected, { status: "NEW" }, "new")}
                  >
                    Sett som ny
                  </Button>
                )}
                {selected.status !== "REJECTED" && (
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={busy === "reject"}
                    onClick={() =>
                      patch(selected, { status: "REJECTED" }, "reject")
                    }
                  >
                    Avvis
                  </Button>
                )}
                {selected.contactEmail && (
                  <a
                    className={`${ui.btn} ${ui.secondary} ${ui.sm}`}
                    href={replyMailto(selected.contactEmail)}
                  >
                    Svar på e-post
                  </a>
                )}
                <Button
                  variant="danger"
                  size="sm"
                  icon={<Icon name="trash" size={16} />}
                  loading={busy === "delete"}
                  onClick={() => remove(selected)}
                >
                  Slett
                </Button>
              </div>

              <dl className={styles.details}>
                <dt>By</dt>
                <dd>{selected.city}</dd>
                <dt>Adresse</dt>
                <dd>{selected.address || "–"}</dd>
                <dt>Nettside</dt>
                <dd>
                  {selected.website ? (
                    <a
                      href={
                        /^https?:\/\//i.test(selected.website)
                          ? selected.website
                          : `https://${selected.website}`
                      }
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      {selected.website}
                    </a>
                  ) : (
                    "–"
                  )}
                </dd>
                <dt>Kontakt</dt>
                <dd>
                  {[
                    selected.contactName,
                    selected.contactEmail,
                    selected.contactPhone,
                  ]
                    .filter(Boolean)
                    .join(" · ") || "–"}
                </dd>
                <dt>Representerer</dt>
                <dd>{selected.isAffiliated ? "Ja, moskeen" : "Nei"}</dd>
                {selected.linkedOrgNr && (
                  <>
                    <dt>Lagt inn som</dt>
                    <dd>
                      {selected.linkedMosqueName
                        ? `${selected.linkedMosqueName} (${formatOrgNumber(selected.linkedOrgNr)})`
                        : formatOrgNumber(selected.linkedOrgNr)}
                    </dd>
                  </>
                )}
                {selected.firebaseUid && (
                  <>
                    <dt>Firebase-UID</dt>
                    <dd>
                      <code>{selected.firebaseUid}</code>
                    </dd>
                  </>
                )}
              </dl>

              {selected.message && (
                <p className={styles.body}>{selected.message}</p>
              )}

              <div className={styles.linkRow}>
                <TextField
                  label="Org.nr i iBønn"
                  placeholder="971234567"
                  value={orgNr}
                  onChange={(e) => setOrgNr(e.target.value)}
                />
                <Button
                  variant="mint"
                  icon={<Icon name="check" size={16} />}
                  loading={busy === "added"}
                  onClick={() => markAdded(selected)}
                >
                  Merk som lagt inn
                </Button>
              </div>

              <Textarea
                label="Notat (bare for superadmin)"
                value={note}
                maxChars={4000}
                rows={3}
                onChange={(e) => setNote(e.target.value)}
              />
              <div className={styles.noteActions}>
                <Button
                  size="sm"
                  disabled={note.trim() === (selected.adminNote ?? "").trim()}
                  loading={busy === "note"}
                  onClick={async () => {
                    if (await patch(selected, { adminNote: note }, "note")) {
                      toast.success("Notatet er lagret.");
                    }
                  }}
                >
                  Lagre notat
                </Button>
              </div>
            </Card>
          ) : (
            <Card>
              <EmptyState icon="mosque" title="Velg et forslag">
                Klikk på et forslag for å se detaljene, opprette moskeen eller
                endre status.
              </EmptyState>
            </Card>
          )}
        </div>
      </div>
    </>
  );
};

export default MosqueSuggestionsPage;
